"use client";

import { DragEvent, MouseEvent, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconGoogleDrive from "@/app/(components)/generics/Icons/IconGoogleDrive";
import IconUpload from "@/app/(components)/generics/Icons/IconUpload";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { useGoogleDrivePicker } from "@/app/hooks/useGoogleDrivePicker";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CallFile } from "@/app/types/call/CallFile";
import { UserCall } from "@/app/types/call/UserCall";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UFile } from "@/app/utils/UFile";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./UploadCalls.module.css";

type Props = DefaultProps & {
  files: CallFile[];
  onFilesAdd: (files: File[]) => void;
  onFileRemove: (id: number) => void;
  onBrowse: () => void;
  onCallCreated?: (call: UserCall) => void;
  isAuthenticated?: boolean;
  onRequireAuth?: () => void;
  uploadError?: string;
  setUploadError: (error: string) => void;
};

export default function UploadCalls(props: Props) {
  const T = useT();
  const {
    params,
    onFilesAdd,
    onBrowse,
    onCallCreated,
    isAuthenticated,
    onRequireAuth,
    uploadError,
    setUploadError,
  } = props;

  const [isDragging, setIsDragging] = useState(false);

  const { openPicker, isScriptLoaded, isPicking } = useGoogleDrivePicker({
    onFilesSelected: (newFiles) => {
      setUploadError("");
      onFilesAdd(newFiles);
    },
    onError: (key) => {
      setUploadError(T(key) as string);
    },
  });

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    onFilesAdd(dropped);
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={UStyles.classNames(styles.dropZone, isDragging ? styles.dragging : "")}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={onBrowse}
      >
        <div className={styles.dropIcon}>
          <IconUpload size={40} />
        </div>

        <p className={styles.dropTitle}>{T(TKey.CALL_ANALYSIS_DROP_TITLE)}</p>
        <p className={styles.dropSubtitle}>{T(TKey.CALL_ANALYSIS_DROP_SUBTITLE)}</p>

        <span className={styles.orSeparator}>{T(TKey.CALL_ANALYSIS_DROP_OR)}</span>

        <Button
          params={params}
          isLink={false}
          type={ButtonType.OUTLINE}
          text={T(TKey.CALL_ANALYSIS_BROWSE) as string}
          min={true}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onBrowse();
          }}
        />

        <div className={styles.formatsText}>
          <strong>{T(TKey.CALL_ANALYSIS_FORMATS)}</strong> {UFile.getCallFormatsLabel()}
          <span className={styles.maxSizeInfo}>{T(TKey.CALL_ANALYSIS_MAX_SIZE)}</span>
        </div>
      </div>

      <div className={styles.integrationsSection}>
        <p className={styles.integrationsTitle}>{T(TKey.CALL_ANALYSIS_INTEGRATIONS_TITLE)}</p>
        <div className={styles.integrationsList}>
          <button
            type="button"
            className={styles.integrationBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setUploadError("");
              if (isAuthenticated === false && onRequireAuth) {
                onRequireAuth();
              } else {
                document.body.style.overflow = "hidden";
                openPicker();
              }
            }}
            disabled={!isScriptLoaded || isPicking}
          >
            {isPicking ? (
              <CircleLoader size={16} color="var(--color-green-3)" />
            ) : (
              <IconGoogleDrive size={16} />
            )}
            Google Drive
          </button>
          <button
            type="button"
            className={styles.integrationBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setUploadError("");
              if (isAuthenticated === false && onRequireAuth) {
                onRequireAuth();
              } else {
                ModalStore.getState().openModal(
                  Components.ImportLinkModal,
                  T(TKey.CALL_ANALYSIS_IMPORT_LINK_TITLE),
                  { params, onFilesAdd, onCallCreated },
                  ModalType.SMALL,
                );
              }
            }}
          >
            {/* Using a simple globe/link icon or generic SVG for now */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            {T(TKey.CALL_ANALYSIS_IMPORT_LINK_BTN)}
          </button>
        </div>
        {uploadError && <ErrorMessage message={uploadError} />}
      </div>
    </div>
  );
}
